'use client';

import { useState, useMemo, useCallback } from 'react';
import { ContentBrowser } from '@/components/content/ContentBrowser';
import { ContentListItem } from '@/components/content/ContentListItem';
import { useUrlSync } from '@/lib/hooks/useUrlSync';
import type { ContentQuery } from '@/lib/api/types';
import { BrowserFilterConfig } from '../ObjectBrowser';
import { CONTENT_TYPE_CONFIG } from '@sizzlenz/shared';

interface LinkedRecord {
  objectType: string;
  objectId: number | string;
}

const CONTENT_TYPE_FILTER: BrowserFilterConfig = {
  key: 'type',
  label: 'Type',
  options: Object.entries(CONTENT_TYPE_CONFIG)
    .filter(([key, config]) => ['news', 'announcement', 'article', 'case-study'].includes(key))
    .map(([key, config]) => ({
      id: key,
      label: config.label,
    })),
};

interface ObjectContentFeedProps {
  /** Owner type for content (e.g. 'entity') */
  ownerType?: string;
  /** Owner ID for content */
  ownerId?: string;
  /** Linked record references to include content linked to these records */
  linkedRecords?: LinkedRecord[];
  /** Additional tags to filter by */
  tags?: string[];
  /** Content types to show. Defaults to news/article/announcement. */
  contentTypes?: string[];
  /** Section title */
  title?: string;
  /** Section description */
  description?: string;
  /** Label for back navigation on content detail pages */
  backLabel?: string;
  /** URL for back navigation on content detail pages */
  backHref?: string;
  /** Page size. Defaults to 20. */
  limit?: number;
  /** Empty state messaging */
  emptyState?: { title: string; description: string; compact?: boolean };
  /** Enable search input. Defaults to true. */
  searchEnabled?: boolean;
  /** Force the filter layout mode. Defaults to 'mobile'. */
  filterLayout?: 'desktop' | 'mobile' | 'auto';
  /** Display view mode. Defaults to 'list'. */
  viewMode?: 'grid' | 'list';
  /** Show the grid/list view mode toggle buttons. Defaults to true. */
  showViewModeToggle?: boolean;
  /** Hide the results header (count + view toggle) when there are no results. Defaults to true. */
  hideHeaderOnEmpty?: boolean;
  /** Control the minimum height behavior. Defaults to 'none' for embedded usage. */
  minHeight?: 'full' | 'none' | string;
  /** Show the thumbnail image in list mode items. Defaults to true. */
  showListImage?: boolean;
  /** Show the result count header. Defaults to true. */
  showResultCount?: boolean;
  /** Sync search/filter state to URL query params. Defaults to true. Set to false for embedded usage where URL changes are unwanted. */
  syncToUrl?: boolean;
  /** Override the URL query parameter names used for syncing. Useful when multiple URL-synced components coexist on the same page. */
  urlParamNames?: {
    /** Query param name for the search value. Defaults to 'contentSearch'. */
    search?: string;
    /** Query param name for the content type filter. Defaults to 'contentType'. */
    contentType?: string;
  };
  /** Scroll to top of the page when search or filters change. Defaults to true when syncToUrl is true, false otherwise. */
  scrollOnChange?: boolean;
}

export function ObjectContentFeed({
  ownerType,
  ownerId,
  linkedRecords,
  tags,
  contentTypes = ['news', 'article', 'announcement', 'case-study'],
  title = 'Content',
  description,
  backLabel,
  backHref,
  limit = 20,
  emptyState = { title: 'No content', description: null, compact: true },
  searchEnabled = true,
  filterLayout = 'mobile',
  viewMode: viewModeProp = 'list',
  showViewModeToggle = true,
  hideHeaderOnEmpty = true,
  minHeight = 'none',
  showListImage = true,
  showResultCount = false,
  syncToUrl = true,
  urlParamNames,
  scrollOnChange,
}: ObjectContentFeedProps) {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(viewModeProp);

  // Local state used when URL syncing is disabled
  const [localSearch, setLocalSearch] = useState('');
  const [localContentType, setLocalContentType] = useState<string[]>([]);

  const searchParamName = urlParamNames?.search ?? 'contentSearch';
  const contentTypeParamName = urlParamNames?.contentType ?? 'contentType';

  const {
    values: urlValues,
    handlers: urlHandlers,
    isReady: urlIsReady,
  } = useUrlSync({
    filters: {
      contentSearch: { paramName: searchParamName, defaultValue: '' },
      contentType: { paramName: contentTypeParamName, defaultValue: [], isArray: true },
    },
  });

  // Pick URL-synced or local values based on syncToUrl
  const searchValue = syncToUrl ? urlValues.contentSearch : localSearch;
  const contentTypeValue = syncToUrl ? urlValues.contentType : localContentType;
  const isReady = syncToUrl ? urlIsReady : true;

  const initialFilters = useMemo(() => {
    const f: Record<string, string[]> = {};
    if (contentTypeValue.length > 0) f.type = contentTypeValue;
    return f;
  }, [contentTypeValue.join(',')]);

  const handleSearchChange = useCallback(
    (search: string) => {
      if (syncToUrl) {
        urlHandlers.contentSearch(search);
      } else {
        setLocalSearch(search);
      }
    },
    [syncToUrl, urlHandlers.contentSearch]
  );

  const handleFiltersChange = useCallback(
    (filters: Record<string, string[]>) => {
      if (syncToUrl) {
        urlHandlers.contentType(filters.type || []);
      } else {
        setLocalContentType(filters.type || []);
      }
    },
    [syncToUrl, urlHandlers.contentType]
  );

  const query: ContentQuery = useMemo(() => {
    const q: ContentQuery = {
      type: contentTypes,
      status: 'published',
      visibility: 'public',
    };
    if (ownerId) q.ownerId = ownerId;
    if (ownerType) q.ownerType = ownerType;
    if (tags) q.tags = tags;
    // When linkedRecords are provided, use includeLinkedTo to OR ownership with linked tag matching
    // We pass the first linked record as the includeLinkedTo filter (the DB query ORs it with ownerId)
    if (linkedRecords && linkedRecords.length > 0) {
      q.includeLinkedToType = linkedRecords[0].objectType;
      q.includeLinkedToId = String(linkedRecords[0].objectId);
    }
    return q;
  }, [ownerType, ownerId, tags, contentTypes, linkedRecords]);

  if (!isReady) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div>
      {title && (
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
          {description && <p className="text-gray-600">{description}</p>}
        </div>
      )}
      <div className="max-w-[1000px]">
        <ContentBrowser
          query={query}
          searchEnabled={searchEnabled}
          searchPlaceholder="Search..."
          showTypeFilter={contentTypes.length > 1}
          filterLayout={filterLayout}
          filters={[CONTENT_TYPE_FILTER]}
          showResultCount={showResultCount}
          limit={limit}
          emptyState={emptyState}
          initialSearch={searchValue}
          initialFilters={initialFilters}
          onSearchChange={handleSearchChange}
          onFiltersChange={handleFiltersChange}
          backLabel={backLabel}
          backHref={backHref}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          showViewModeToggle={showViewModeToggle}
          hideHeaderOnEmpty={hideHeaderOnEmpty}
          minHeight={minHeight}
          scrollOnChange={scrollOnChange ?? syncToUrl}
          renderListItem={(content) => (
            <ContentListItem
              content={content}
              backLabel={backLabel}
              backHref={backHref}
              showImage={showListImage}
            />
          )}
        />
      </div>
    </div>
  );
}
